'use client';
import React, { useState } from 'react';
import cn from '@utils/cn';
import { ArrowLeftIcon, ArrowRightIcon } from '@sanity/icons';
import { TestimonialsType } from '@lib/sanity/queries/modules/testimonials/types';
import { Button } from '@ui/Button/Button';
import Anchor from '@ui/Anchor/Anchor';
import { GridColFullWidth, GridContainerColClasses } from '@constants/layout';
import Text, { TextVariant } from '@ui/Text/Text';
import PortableText from '@ui/Text/PortableText';

type Props = {
  visuals?: Record<string, React.ReactNode>;
  data: TestimonialsType;
};

export default function TestimonialsCaseStudiesClient({ data, visuals }: Props) {
  const [currentTestimonialIndex, setCurrentTestimonialIndex] = useState(0);
  if (!data.testimonials || !data.testimonials?.length) {
    return null;
  }
  const testimonials = data.testimonials;

  const handleMoveForward = () => {
    setCurrentTestimonialIndex(currentTestimonialIndex === testimonials?.length - 1 ? 0 : currentTestimonialIndex + 1);
  };
  const handleMoveBackwards = () => {
    setCurrentTestimonialIndex(currentTestimonialIndex === 0 ? testimonials.length - 1 : currentTestimonialIndex - 1);
  };
  const selectedTestimonial = testimonials[currentTestimonialIndex];
  return (
    <div className={cn('flex flex-col gap-12', GridColFullWidth)}>
      {testimonials.length > 1 && (
        <div
          className={cn(
            'flex gap-3 lg:hidden',
            'w-full justify-start md:justify-end ',
            'col-span-4 sm:col-span-6 md:col-span-8 lg:col-span-2',
            'order-2 md:order-1 lg:order-2',
          )}
        >
          <Button aria-label="Left" size="icon" variant="outline" onClick={handleMoveBackwards}>
            <ArrowLeftIcon width={24} height={24} className="text-foreground" />
          </Button>
          <Button aria-label="Right" size="icon" variant="outline" onClick={handleMoveForward}>
            <ArrowRightIcon width={24} height={24} className="text-foreground" />
          </Button>
        </div>
      )}

      <div className={cn('relative', 'order-1 md:order-2 lg:order-1', GridContainerColClasses)}>
        <div
          className={cn(
            'flex flex-col gap-8',
            'items-start justify-center  align-middle',
            'col-span-4 sm:col-span-6 md:col-span-4',
            'pb-8 md:pb-0',
          )}
        >
          <div className="flex flex-col gap-2">
            <PortableText content={data.title} variant={TextVariant.TitleSM} />
            <PortableText content={data.description} variant={TextVariant.TextBodyLG} />
          </div>
          {data.buttonPrimary && data.buttonPrimary.text && (
            <Anchor
              aria-label={data.buttonPrimary.text.value ?? ''}
              href={data.buttonPrimary.url || undefined}
              className="w-full sm:w-4/6 md:w-auto"
            >
              <Button size="l" variant="outline" className="sm:w-full md:w-auto">
                {data.buttonPrimary.text.value}
              </Button>
            </Anchor>
          )}
        </div>

        <div className="relative col-span-4 box-border sm:col-span-6 md:col-span-4 lg:col-span-6">
          {selectedTestimonial?.image?._id && visuals?.[selectedTestimonial?.image?._id]}
        </div>

        <div
          className={cn(
            'bg-primary w-full p-8',
            'relative bottom-0 right-0 lg:absolute',
            'col-span-4 sm:col-span-6 md:col-span-5 md:col-start-4 lg:col-span-5 lg:col-start-8',
          )}
        >
          <PortableText
            content={selectedTestimonial.quote}
            variant={TextVariant.TextBlockquoteSM}
            className="text-primary-foreground"
          />
          <Anchor className="flex w-full flex-col" href={selectedTestimonial.company?.url || undefined}>
            <Text
              content={selectedTestimonial.name?.value}
              variant={TextVariant.SubtitleMD}
              className="text-primary-foreground "
            />
            <Text
              content={selectedTestimonial.company?.text?.value}
              variant={TextVariant.SubtitleMD}
              className="text-primary-foreground "
            />{' '}
          </Anchor>
        </div>
        {testimonials.length > 1 && (
          <div className={cn('hidden gap-3 lg:flex', 'lg:col-span-2', 'w-full justify-start md:justify-end')}>
            <Button size="icon" variant="outline" onClick={handleMoveBackwards} aria-label="Previous">
              <ArrowLeftIcon width={24} height={24} className="text-foreground" />
            </Button>
            <Button size="icon" variant="outline" onClick={handleMoveForward} aria-label="Next">
              <ArrowRightIcon width={24} height={24} className="text-foreground" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
